// src/components/WabaAccountsPanel.jsx
//
// Register the WhatsApp Business Accounts the platform can send from.
//
// Lives on the Settings page, below the WhatsApp Business API section that
// still owns the original single account. That account appears here too, as a
// read-only row — its credentials are held by that section rather than by the
// account list, so editing it in two places would give it two sources of
// truth. The row is labelled by the business it belongs to, not by where the
// credentials are stored.
//
// The writes here go through the same one-time-code unlock as the rest of
// Settings; the server enforces it, and a 403 sends the operator back to the
// code screen the same way saving settings does.

import React, { useEffect, useState } from 'react';
import {
  FiSmartphone,
  FiPlus,
  FiTrash2,
  FiCheck,
  FiEdit2,
  FiX,
  FiZap,
  FiStar,
} from 'react-icons/fi';

import { apiFetch } from '../lib/api';
import { refreshAccounts } from '../lib/waba';

const EMPTY = {
  name: '',
  label: '',
  phone_number_id: '',
  business_account_id: '',
  access_token: '',
  api_url: '',
  make_default: false,
};

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-brand-500';

export default function WabaAccountsPanel({ onLocked = () => {} }) {
  const [accounts, setAccounts] = useState(null);
  const [defaultName, setDefaultName] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  // `editing` is the original name of the row being edited, or '' when the
  // form is adding. Kept separate from the draft so a rename knows which row
  // it replaces.
  const [editing, setEditing] = useState('');
  const [draft, setDraft] = useState(EMPTY);
  const [testing, setTesting] = useState('');

  const load = async () => {
    try {
      const out = await apiFetch('/waba-accounts');
      setAccounts(out.accounts || []);
      setDefaultName(out.default || '');
      refreshAccounts();      // other pages must not keep showing a stale list
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const fail = (e) => {
    setError(e.message || String(e));
    // The unlock expires server-side; surface that as the lock screen rather
    // than as a save button that keeps refusing.
    if (/locked/i.test(e.message || '')) onLocked();
  };

  const startAdd = () => { setEditing(''); setDraft(EMPTY); setNotice(''); setError(''); };

  const startEdit = (a) => {
    setEditing(a.name);
    setDraft({
      name: a.name,
      label: a.label || '',
      phone_number_id: a.phone_number_id || '',
      business_account_id: a.business_account_id || '',
      access_token: '',                    // never prefilled; blank keeps the stored one
      api_url: a.api_url || '',
      make_default: a.name === defaultName,
    });
    setNotice('');
    setError('');
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setNotice('');
    try {
      const path = editing ? `/waba-accounts/${encodeURIComponent(editing)}` : '/waba-accounts';
      const out = await apiFetch(path, {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify(draft),
      });
      setNotice(out.message || 'Saved.');
      setDraft(EMPTY);
      setEditing('');
      await load();
    } catch (err) { fail(err); } finally { setBusy(false); }
  };

  const remove = async (a) => {
    const ok = window.confirm(
      `Remove '${a.name}'?\n\nCampaigns still set to this account will fail to send ` +
      `until they are moved to another one. Templates on the WhatsApp account itself ` +
      `are not touched.`
    );
    if (!ok) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const out = await apiFetch(`/waba-accounts/${encodeURIComponent(a.name)}`, {
        method: 'DELETE',
      });
      setNotice(out.message || 'Removed.');
      await load();
    } catch (err) { fail(err); } finally { setBusy(false); }
  };

  const makeDefault = async (a) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const out = await apiFetch(`/waba-accounts/${encodeURIComponent(a.name)}/default`, {
        method: 'POST',
      });
      setNotice(out.message || 'Default updated.');
      await load();
    } catch (err) { fail(err); } finally { setBusy(false); }
  };

  // Tests the typed-in credentials when the form is open for that account, and
  // the stored ones otherwise — so a token can be checked before it is saved.
  const test = async (name) => {
    setTesting(name); setError(''); setNotice('');
    const body =
      (editing || draft.name) === name
        ? {
            phone_number_id: draft.phone_number_id || undefined,
            business_account_id: draft.business_account_id || undefined,
            access_token: draft.access_token || undefined,
            api_url: draft.api_url || undefined,
          }
        : {};
    try {
      const out = await apiFetch(`/waba-accounts/${encodeURIComponent(name)}/test`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setNotice(
        `${name}: ${out.message}` +
        (typeof out.templates_visible === 'number'
          ? ` Templates are readable on this account.`
          : '')
      );
    } catch (err) { setError(`${name}: ${err.message}`); } finally { setTesting(''); }
  };

  const set = (k) => (e) =>
    setDraft((d) => ({ ...d, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div className="mt-8 border-t border-gray-200 pt-6">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <FiSmartphone className="h-5 w-5 text-gray-600" />
        WhatsApp Business Accounts
      </h2>
      <p className="mb-4 text-sm text-gray-500">
        Each account is a WhatsApp Business number the platform can send from.
        Give it a short unique name — <code className="rounded bg-gray-100 px-1">sasakwaba</code> —
        and the sending, template and campaign APIs accept that name as{' '}
        <code className="rounded bg-gray-100 px-1">waba_account</code>. Requests that
        name none use the default. The delivery workers pick up a change within a minute.
      </p>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && !error && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {notice}
        </div>
      )}

      {accounts === null ? (
        <div className="py-6 text-center text-gray-500">Loading accounts…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Phone number ID</th>
                <th className="py-2 pr-4 font-medium">Business account ID</th>
                <th className="py-2 pr-4 font-medium">Token</th>
                <th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-500">
                    No WhatsApp accounts configured yet.
                  </td>
                </tr>
              )}
              {accounts.map((a) => (
                <tr key={a.name}>
                  <td className="py-3 pr-4">
                    <div className="font-semibold text-gray-900">{a.name}</div>
                    {a.label && <div className="text-xs text-gray-500">{a.label}</div>}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {a.name === defaultName && (
                        <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                          default
                        </span>
                      )}
                      {/* Says why Edit and Delete are disabled on this row,
                          without naming the file its credentials live in —
                          that is deployment detail, not something an operator
                          reading a table of business accounts needs. */}
                      {a.is_legacy && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          platform account
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-gray-700">
                    {a.phone_number_id || '—'}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-gray-700">
                    {a.business_account_id || '—'}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-gray-700">
                    {a.token_set ? a.token_hint : (
                      <span className="text-amber-700">not set</span>
                    )}
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        disabled={busy || testing === a.name}
                        onClick={() => test(a.name)}
                        title="Check these credentials against the WhatsApp API"
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        <FiZap className="h-4 w-4" />
                        {testing === a.name ? 'Testing…' : 'Test'}
                      </button>
                      <button
                        type="button"
                        disabled={busy || a.name === defaultName}
                        onClick={() => makeDefault(a)}
                        title="Send from this account when a request names none"
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        <FiStar className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={busy || a.is_legacy}
                        onClick={() => startEdit(a)}
                        title={a.is_legacy
                          ? 'Edit this one in the WhatsApp Business API section above'
                          : 'Edit this account'}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={busy || a.is_legacy}
                        onClick={() => remove(a)}
                        title={a.is_legacy
                          ? 'The platform account cannot be removed here'
                          : 'Remove this account'}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form
        onSubmit={save}
        className="mt-6 grid grid-cols-1 gap-4 rounded-xl bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {editing ? `Edit '${editing}'` : 'Add an account'}
          </h3>
          {editing && (
            <button
              type="button"
              onClick={startAdd}
              className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-gray-900"
            >
              <FiX className="h-4 w-4" />
              Cancel edit
            </button>
          )}
        </div>

        <Field
          label="Unique name"
          hint="Lowercase letters, digits, - or _. This is what the APIs accept as waba_account."
        >
          <input
            type="text"
            placeholder="sasakwaba"
            value={draft.name}
            onChange={set('name')}
            className={inputClass}
            required
          />
        </Field>

        <Field label="Display name" hint="Shown in dropdowns. Optional.">
          <input
            type="text"
            placeholder="Sasak Retail"
            value={draft.label}
            onChange={set('label')}
            className={inputClass}
          />
        </Field>

        <Field label="Phone Number ID" hint="From Meta Business Manager.">
          <input
            type="text"
            value={draft.phone_number_id}
            onChange={set('phone_number_id')}
            className={inputClass}
            required
          />
        </Field>

        <Field label="Business Account ID" hint="The WABA id that owns the templates.">
          <input
            type="text"
            value={draft.business_account_id}
            onChange={set('business_account_id')}
            className={inputClass}
            required
          />
        </Field>

        <Field
          label="Access Token"
          hint={editing
            ? 'Leave empty to keep the stored token.'
            : 'A permanent token for this account.'}
        >
          <input
            type="password"
            autoComplete="new-password"
            value={draft.access_token}
            onChange={set('access_token')}
            className={inputClass}
            required={!editing}
          />
        </Field>

        <Field
          label="Graph API URL"
          hint="Optional. Empty uses the platform's API Base URL above."
        >
          <input
            type="url"
            placeholder="https://graph.facebook.com/v23.0"
            value={draft.api_url}
            onChange={set('api_url')}
            className={inputClass}
          />
        </Field>

        <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
          <input
            id="waba-make-default"
            type="checkbox"
            checked={draft.make_default}
            onChange={set('make_default')}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="waba-make-default" className="text-sm text-gray-700">
            Send from this account when a request names none
          </label>
        </div>

        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3">
          <button
            type="submit"
            disabled={busy}
            className="flex min-h-[42px] items-center justify-center gap-2 rounded-lg px-4 font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: '#F96168' }}
          >
            {editing ? <FiCheck className="h-4 w-4" /> : <FiPlus className="h-4 w-4" />}
            {busy ? 'Working…' : editing ? 'Save account' : 'Add account'}
          </button>
          {draft.name && (
            <button
              type="button"
              disabled={busy || testing === draft.name}
              onClick={() => test(draft.name)}
              className="flex min-h-[42px] items-center gap-2 rounded-lg border border-gray-300 px-4 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <FiZap className="h-4 w-4" />
              {testing === draft.name ? 'Testing…' : 'Test these credentials'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
